/**
 * =====================================================================================
 * SERVICIO DE EMISORES - emisores.service.js
 * =====================================================================================
 *
 * Este servicio gestiona los EMISORES, que son las empresas o personas naturales
 * que emiten documentos tributarios electrónicos (DTEs).
 *
 * ¿QUÉ ES UN EMISOR?
 * ------------------
 * En el contexto de facturación electrónica, el EMISOR es quien emite la factura.
 * Es decir, es TU EMPRESA o la empresa que usa este sistema para facturar.
 *
 * DATOS TÍPICOS DE UN EMISOR:
 * ---------------------------
 * - NIT (Número de Identificación Tributaria)
 * - NRC (Número de Registro de Contribuyente)
 * - Nombre o Razón Social
 * - Nombre Comercial
 * - Actividad Económica
 * - Dirección (departamento, municipio, complemento)
 * - Teléfono
 * - Correo electrónico
 *
 * RELACIÓN CON OTROS CONCEPTOS:
 * -----------------------------
 *
 * EMISOR (quien emite)
 *    │
 *    ├── tiene una o más → SUCURSALES
 *    │                       │
 *    │                       └── cada sucursal emite → FACTURAS
 *    │
 *    └── sus datos aparecen en → TODOS LOS DTEs emitidos
 *
 * EMISOR vs RECEPTOR:
 * - EMISOR: La empresa que VENDE y emite la factura (tú)
 * - RECEPTOR: El cliente que COMPRA y recibe la factura
 *
 * FLUJO EN EL SISTEMA:
 * --------------------
 *
 * 1. Al iniciar sesión, se carga el emisor asociado al usuario
 * 2. Los datos del emisor se usan en:
 *    - Header de cada factura (datos de quien emite)
 *    - Generación del número de control
 *    - Transmisión a Hacienda
 *
 * RELACIÓN CON OTROS ARCHIVOS:
 * ----------------------------
 * - api.js: Cliente HTTP para las peticiones
 * - src/store/modules/emisor.js: Almacena el emisor actual en estado global
 * - src/views/configuracion/EmisorView.vue: Vista para editar datos del emisor
 * - Backend: Controllers/EmisoresController.cs
 *
 * =====================================================================================
 */

// Importamos la instancia de Axios configurada
import api from './api'

/**
 * =====================================================================================
 * OBJETO DEL SERVICIO DE EMISORES
 * =====================================================================================
 *
 * Proporciona operaciones CRUD (Create, Read, Update, Delete) para emisores.
 * En la mayoría de casos, una instalación tendrá UN solo emisor (la empresa),
 * pero el sistema permite gestionar múltiples emisores si es necesario
 * (ej: un contador que maneja varias empresas).
 */
const emisoresService = {
  /**
   * ===========================================================================
   * MÉTODO: getAll
   * ===========================================================================
   *
   * Obtiene la lista de todos los emisores registrados en el sistema.
   * SOLO para SuperAdmin.
   *
   * ENDPOINT: GET /api/emisores
   *
   * @returns {Promise<Object>} Respuesta paginada con items y metadata
   */
  async getAll(pageNumber = 1, pageSize = 10, includeInactive = false) {
    const response = await api.get('/emisores', {
      params: { pageNumber, pageSize, includeInactive }
    })
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: getMiPerfil
   * ===========================================================================
   *
   * Obtiene el perfil del emisor del usuario actual.
   * Para EmisorAdmin y GerenteSucursal.
   *
   * ENDPOINT: GET /api/emisores/mi-perfil
   *
   * @returns {Promise<Object>} Datos del emisor del usuario actual
   */
  async getMiPerfil() {
    const response = await api.get('/emisores/mi-perfil')
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: updateMiPerfil
   * ===========================================================================
   *
   * Actualiza el perfil del emisor del usuario actual.
   * Solo para EmisorAdmin.
   *
   * ENDPOINT: PUT /api/emisores/mi-perfil
   *
   * @param {Object} data - Datos a actualizar (nombreComercial, correoElectronico, telefono, direccion)
   * @returns {Promise<Object>} Emisor actualizado
   */
  async updateMiPerfil(data) {
    const response = await api.put('/emisores/mi-perfil', data)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: getById
   * ===========================================================================
   *
   * Obtiene un emisor específico por su ID.
   *
   * ENDPOINT: GET /api/emisores/{id}
   *
   * CASO DE USO PRINCIPAL:
   * Al iniciar sesión, el usuario tiene asociado un emisorId.
   * Este método obtiene los datos completos de ese emisor.
   *
   * @param {number} id - ID del emisor
   * @returns {Promise<Object>} Datos completos del emisor
   *
   * EJEMPLO DE RESPUESTA:
   * {
   *   id: 1,
   *   nit: "06140101901010",
   *   nrc: "123456-7",
   *   nombre: "Mi Empresa S.A. de C.V.",
   *   nombreComercial: "Mi Empresa",
   *   actividadEconomica: { codigo: "46101", valor: "Venta al por mayor" },
   *   direccion: {
   *     departamento: "06",
   *     municipio: "14",
   *     complemento: "Calle Principal #123"
   *   },
   *   telefono: "2222-3333",
   *   correo: "info@miempresa.com"
   * }
   */
  async getById(id) {
    const response = await api.get(`/emisores/${id}`)
    return response.data
  },

  /**
   * UsuarioCompartido + Plan B Hub-as-Emisor: lista la metadata (id, nombre,
   * NIT) de los Emisores que vienen en el claim `emisores_accesibles` del
   * JWT actual. Cualquier rol logueado puede llamarlo; el BE filtra por el
   * claim, no por el rol. Lo usa el selector de Emisor del HubCallbackView
   * para mostrar nombres en lugar de "Emisor #1".
   *
   * @returns {Promise<Array<{id: number, nombreRazonSocial: string, nit: string}>>}
   */
  async listarAccesibles() {
    const response = await api.get('/emisores/accesibles')
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: create
   * ===========================================================================
   *
   * Crea un nuevo emisor en el sistema.
   *
   * ENDPOINT: POST /api/emisores
   *
   * CUANDO SE USA:
   * - Configuración inicial del sistema
   * - Agregar nueva empresa en sistema multi-empresa
   *
   * VALIDACIONES DEL BACKEND:
   * - NIT único (no puede haber dos emisores con el mismo NIT)
   * - NRC único
   * - Todos los campos requeridos presentes
   *
   * @param {Object} emisor - Datos del emisor a crear
   * @returns {Promise<Object>} Emisor creado con su ID asignado
   */
  async create(emisor) {
    // Transformar a PascalCase para el backend .NET
    // NIT: quitar guiones, MH requiere 9-14 dígitos sin guiones
    // NRC: solo dígitos, MH requiere 2-8 dígitos sin guiones
    const dto = {
      Nit: emisor.nit ? emisor.nit.replace(/-/g, '') : null,
      Nrc: emisor.nrc ? emisor.nrc.replace(/[^0-9]/g, '') : null,
      NombreRazonSocial: emisor.nombreRazonSocial,
      NombreComercial: emisor.nombreComercial || null,
      CodigoActividad: emisor.codigoActividad,
      DescripcionActividad: emisor.descripcionActividad || null,
      CatDepartamentoId: emisor.catDepartamentoId,
      CatMunicipioId: emisor.catMunicipioId,
      CatDistritoId: emisor.catDistritoId || null,
      CatTipoEstablecimientoId: emisor.catTipoEstablecimientoId,
      Direccion: emisor.direccion,
      Telefono: emisor.telefono ? emisor.telefono.replace(/-/g, '') : null,
      CorreoElectronico: emisor.correoElectronico,
      // Códigos de Establecimiento MH
      CodEstableMH: emisor.codEstableMH || null,
      CodEstable: emisor.codEstable || null,
      CodPuntoVentaMH: emisor.codPuntoVentaMH || null,
      CodPuntoVenta: emisor.codPuntoVenta || null,
      // Configuración Ministerio de Hacienda
      CatAmbienteDestinoId: 1, // Siempre Pruebas al crear, se cambia desde el login
      MhUsuario: emisor.mhUsuario || null,
      MhClaveApi: emisor.mhClaveApi || null,
      MhLlavePrivada: emisor.mhLlavePrivada || null,
      MhLlavePublica: emisor.mhLlavePublica || null,
      // Credenciales MH Producción
      MhUsuarioProd: emisor.mhUsuarioProd || null,
      MhClaveApiProd: emisor.mhClaveApiProd || null,
      MhLlavePrivadaProd: emisor.mhLlavePrivadaProd || null,
      MhLlavePublicaProd: emisor.mhLlavePublicaProd || null,
      // Configuración SMTP (SmtpUser = correoElectronico, solo enviar SmtpPassword si se proporcionó)
      SmtpUser: emisor.smtpUser || null,
      SmtpPassword: emisor.smtpPassword || null,
      EmailRemitente: emisor.emailRemitente || null
    }
    const response = await api.post('/emisores', dto)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: update
   * ===========================================================================
   *
   * Actualiza los datos de un emisor existente.
   *
   * ENDPOINT: PUT /api/emisores/{id}
   *
   * CASOS DE USO:
   * - Cambio de dirección de la empresa
   * - Actualización de teléfono o correo
   * - Agregar nombre comercial
   *
   * IMPORTANTE:
   * Algunos datos como NIT y NRC normalmente NO deberían cambiar
   * una vez registrados, ya que son asignados por Hacienda.
   * El backend puede restringir la modificación de estos campos.
   *
   * @param {number} id - ID del emisor a actualizar
   * @param {Object} emisor - Datos actualizados del emisor
   * @returns {Promise<Object>} Emisor con los datos actualizados
   */
  async update(id, emisor) {
    // Transformar a PascalCase para el backend .NET
    // NIT: quitar guiones, MH requiere 9-14 dígitos sin guiones
    // NRC: solo dígitos, MH requiere 2-8 dígitos sin guiones
    const dto = {
      Id: id,
      Nit: emisor.nit ? emisor.nit.replace(/-/g, '') : null,
      Nrc: emisor.nrc ? emisor.nrc.replace(/[^0-9]/g, '') : null,
      NombreRazonSocial: emisor.nombreRazonSocial,
      NombreComercial: emisor.nombreComercial || null,
      CodigoActividad: emisor.codigoActividad,
      DescripcionActividad: emisor.descripcionActividad || null,
      CatDepartamentoId: emisor.catDepartamentoId,
      CatMunicipioId: emisor.catMunicipioId,
      CatDistritoId: emisor.catDistritoId || null,
      CatTipoEstablecimientoId: emisor.catTipoEstablecimientoId,
      Direccion: emisor.direccion,
      Telefono: emisor.telefono ? emisor.telefono.replace(/-/g, '') : null,
      CorreoElectronico: emisor.correoElectronico,
      // Códigos de Establecimiento MH
      CodEstableMH: emisor.codEstableMH || null,
      CodEstable: emisor.codEstable || null,
      CodPuntoVentaMH: emisor.codPuntoVentaMH || null,
      CodPuntoVenta: emisor.codPuntoVenta || null,
      // Campo Activo para permitir activar/desactivar emisores
      Activo: emisor.activo !== undefined ? emisor.activo : true,
      // Configuración Ministerio de Hacienda (CatAmbienteDestinoId se controla desde el login, no se envía en update)
      MhUsuario: emisor.mhUsuario || null,
      MhClaveApi: emisor.mhClaveApi || null,
      MhLlavePrivada: emisor.mhLlavePrivada || null,
      MhLlavePublica: emisor.mhLlavePublica || null,
      // Credenciales MH Producción
      MhUsuarioProd: emisor.mhUsuarioProd || null,
      MhClaveApiProd: emisor.mhClaveApiProd || null,
      MhLlavePrivadaProd: emisor.mhLlavePrivadaProd || null,
      MhLlavePublicaProd: emisor.mhLlavePublicaProd || null,
      // Configuración SMTP (SmtpUser = correoElectronico, solo enviar SmtpPassword si se proporcionó)
      SmtpUser: emisor.smtpUser || null,
      SmtpPassword: emisor.smtpPassword || null,
      EmailRemitente: emisor.emailRemitente || null,
      // Lectura de DTEs desde correo
      LecturaCorreoHabilitada: emisor.lecturaCorreoHabilitada ?? false
    }
    const response = await api.put(`/emisores/${id}`, dto)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: delete
   * ===========================================================================
   *
   * Elimina un emisor del sistema.
   *
   * ENDPOINT: DELETE /api/emisores/{id}
   *
   * ADVERTENCIA:
   * Esta operación es PELIGROSA y normalmente está restringida porque:
   * 1. Un emisor con facturas NO puede eliminarse (integridad referencial)
   * 2. Las facturas emitidas deben mantenerse por requisitos legales
   * 3. Solo debería usarse para emisores creados por error sin facturas
   *
   * El backend probablemente hará un "soft delete" (marcar como inactivo)
   * en lugar de eliminar físicamente el registro.
   *
   * @param {number} id - ID del emisor a eliminar
   * @returns {Promise<void>} No retorna datos
   */
  async delete(id) {
    // DELETE no retorna body, por eso no hacemos return response.data
    await api.delete(`/emisores/${id}`)
  },

  /**
   * ===========================================================================
   * MÉTODO: toggleActive
   * ===========================================================================
   *
   * Activa o desactiva un emisor.
   *
   * ENDPOINT: PATCH /api/emisores/{id}/toggle-active
   *
   * @param {number} id - ID del emisor
   * @returns {Promise<Object>} { id, activo: boolean }
   */
  async toggleActive(id) {
    const response = await api.patch(`/emisores/${id}/toggle-active`)
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: uploadLogo
   * ===========================================================================
   *
   * Sube el logo del emisor actual.
   * El backend obtiene el emisorId del usuario autenticado.
   *
   * ENDPOINT: POST /api/emisores/logo
   *
   * @param {File} file - Archivo de imagen (PNG, JPG, SVG)
   * @returns {Promise<Object>} { Url: string }
   */
  async uploadLogo(file) {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/emisores/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    // El backend retorna { Url: "..." }, normalizar a logoUrl
    return { logoUrl: response.data.Url || response.data.url }
  },

  /**
   * ===========================================================================
   * MÉTODO: uploadLogoForEmisor
   * ===========================================================================
   *
   * Sube el logo de un emisor específico (Solo SuperAdmin).
   * Permite al SuperAdmin cambiar el logo de cualquier emisor.
   *
   * ENDPOINT: POST /api/emisores/{id}/logo
   *
   * @param {number} emisorId - ID del emisor al que se le subirá el logo
   * @param {File} file - Archivo de imagen (PNG, JPG, SVG)
   * @returns {Promise<Object>} { logoUrl: string }
   */
  async uploadLogoForEmisor(emisorId, file) {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post(`/emisores/${emisorId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    // El backend retorna { url: "..." }, normalizar a logoUrl
    return { logoUrl: response.data.url || response.data.Url }
  },

  /**
   * ===========================================================================
   * MÉTODO: uploadSystemLogo
   * ===========================================================================
   *
   * Sube el logo del sistema (solo SuperAdmin).
   *
   * ENDPOINT: POST /api/emisores/system-logo
   *
   * @param {File} file - Archivo de imagen (PNG, JPG, SVG)
   * @returns {Promise<Object>} { url: string }
   */
  async uploadSystemLogo(file) {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/emisores/system-logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  },

  /**
   * ===========================================================================
   * MÉTODO: getSystemLogo
   * ===========================================================================
   *
   * Obtiene la URL del logo del sistema.
   *
   * ENDPOINT: GET /api/emisores/system-logo
   *
   * @returns {Promise<Object>} { url: string }
   */
  async getSystemLogo() {
    const response = await api.get('/emisores/system-logo')
    return response.data
  },

  // ====== GMAIL OAUTH2 ======

  async getGmailConnectUrl() {
    const response = await api.get('/emisores/gmail-connect')
    return response.data
  },

  async disconnectGmail() {
    const response = await api.delete('/emisores/gmail-disconnect')
    return response.data
  },

  async exchangeGmailCode(code, state) {
    const response = await api.post('/emisores/gmail-exchange-code', { code, state })
    return response.data
  }
}

/**
 * =====================================================================================
 * EXPORTACIÓN DEL SERVICIO
 * =====================================================================================
 *
 * USO EN OTROS ARCHIVOS:
 * ----------------------
 * import emisoresService from '@/services/emisores.service'
 *
 * // Obtener emisor actual
 * const emisor = await emisoresService.getById(usuarioActual.emisorId)
 *
 * // Actualizar datos del emisor
 * await emisoresService.update(1, { telefono: '2222-4444' })
 *
 * ARCHIVOS QUE USAN ESTE SERVICIO:
 * --------------------------------
 * - src/store/modules/emisor.js → Carga y almacena el emisor actual
 * - src/views/configuracion/EmisorView.vue → Formulario de edición
 * - src/components/emision/*.vue → Obtienen datos para mostrar en facturas
 */
export default emisoresService
